import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Menu, X, ChevronRight, ArrowLeft } from "lucide-react";
import { safeBack } from "@/lib/safeBack";
import { useUiStructure } from "@/contexts/UiOverridesContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { Button } from "@/components/ui/button";
import { LangSwitcher } from "@/components/LangSwitcher";
import { AccessibilityMenu } from "@/components/AccessibilityMenu";
import { useLang } from "@/contexts/LanguageContext";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { USE_CASE_NAV_SECTIONS } from "@/data/useCases";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grouped nav structure (IA redesign): How It Works / Features / Solutions /
// Resources dropdowns + direct Pricing & Demos links. Top-level labels are
// i18n keys; child items stay English (site-wide pattern for deep links).
const SUPPORT_WHATSAPP = "https://wa.me/919137774777";
const SUPPORT_EMAIL = "mailto:Support@familyrepository.org";

export const NAV_STRUCTURE = [
    { id: "home", label: "Home", to: "/" },
    {
        id: "about",
        labelKey: "navAbout",
        children: [
            { label: "Company Information", to: "/about" },
            { label: "Founder Appeal", to: "/founder-letter" },
            { label: "Vision", to: "/vision" },
            { label: "Mission", to: "/mission" },
            { label: "Philosophy", to: "/info/philosophy" },
            { label: "Mentor Advisory Board", to: "/info/mentor-advisory-board" },
        ],
    },
    {
        id: "products",
        labelKey: "navProducts",
        children: [
            { label: "Family Repository", to: "/features" },
            { label: "Biography Writing Platform", to: "/expert-writer-workflow" },
            { label: "Personalised Gifting Service", to: "/service/personalised-gifting-service" },
            { label: "Memoir Writing Service", to: "/service/memoir-writing-service" },
            { label: "Age Wave Social Network", to: "/info/age-wave-social-network" },
            { label: "Personal Digital Identity Services", to: "/service/personal-digital-identity-platform" },
            { label: "Doctors Digital Identity Platform", to: "/info/doctors-digital-identity-platform" },
        ],
    },
    {
        id: "how",
        labelKey: "navHowItWorks",
        children: [
            { label: "Simple Five Step", to: "/#how-it-works" },
            { label: "Generate Demo Tree", to: "/#try-demo" },
            { label: "Demo Videos", to: "/demos" },
        ],
    },
    {
        id: "features",
        labelKey: "navFeatures",
        children: [
            { label: "Family Tree Builder", to: "/features#core" },
            { label: "Memories and Stories Vault", to: "/features#memories" },
            { label: "Legal and Legacy Vault", to: "/features#vault" },
            { label: "Family Health Repository", to: "/features#health" },
            { label: "Culture Traditions & Rituals Vault", to: "/features#culture" },
            { label: "Special AI Tools for FT", to: "/features#ai" },
            { label: "100 + Other Features", to: "/features" },
        ],
    },
    {
        id: "usecases",
        label: "Use Cases",
        sections: USE_CASE_NAV_SECTIONS,
    },
    {
        id: "help",
        labelKey: "navHelp",
        children: [
            { label: "Help Center", to: "/help" },
            { label: "FAQ", to: "/faq" },
            { label: "Raise Ticket", to: "/support" },
            { label: "WA Message", to: SUPPORT_WHATSAPP, external: true },
            { label: "Mail Customer Care", to: SUPPORT_EMAIL, external: true },
            { label: "AI BOT - Smart Answers", to: "/faq#ask" },
        ],
    },
    {
        id: "package",
        labelKey: "navPackage",
        children: [
            { label: "Free Service", to: "/pricing#free" },
            { label: "Basic Package", to: "/pricing#basic" },
            { label: "Classic Package", to: "/pricing#classic" },
            { label: "Premium Package", to: "/pricing#premium" },
            { label: "Elite Package", to: "/pricing#elite" },
            { label: "Comparison in Packages", to: "/platform-comparison" },
        ],
    },
    {
        id: "demos",
        labelKey: "navDemos",
        children: [
            { label: "Basic Information Demos", to: "/demos?cat=basic" },
            { label: "Classic Services Demos", to: "/demos?cat=classic" },
            { label: "Premium Service Demos", to: "/demos?cat=premium" },
            { label: "Elite Service Demo", to: "/demos?cat=elite" },
        ],
    },
];

// Navigate helper: handles external links, landing-section hashes and routes.
const goTo = (navigate, to) => {
    if (!to) return;
    if (/^(https?:|mailto:|tel:)/.test(to)) {
        window.open(to, to.startsWith("http") ? "_blank" : "_self", "noopener,noreferrer");
        return;
    }
    if (to.startsWith("/#")) {
        const id = to.slice(2);
        navigate("/");
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return;
    }
    if (to.includes("#")) {
        const [path, hash] = to.split("#");
        navigate(path);
        setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return;
    }
    navigate(to);
};

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "");

const flatChildren = (item) =>
    item.children || (item.sections ? item.sections.flatMap((s) => s.items) : []);

const isChildActive = (to, pathname) => {
    const base = (to || "").split("#")[0];
    if (!base || base === "/") return false;
    return pathname === base || pathname.startsWith(`${base}/`);
};

export const LandingLogo = () => {
    const { app_name, tagline, logo } = useBranding();
    const [isWide, setIsWide] = useState(false);
    if (logo && isWide) {
        return (
            <span className="flex items-center">
                <img
                    src={logo}
                    alt={app_name}
                    onLoad={(e) => setIsWide(e.target.naturalWidth / e.target.naturalHeight >= 1.8)}
                    className="h-10 sm:h-12 w-auto max-w-[210px] sm:max-w-[260px] object-contain"
                />
            </span>
        );
    }
    return (
        <span className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <span className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center overflow-hidden shrink-0">
                {logo ? (
                    <img
                        src={logo}
                        alt={app_name}
                        onLoad={(e) => setIsWide(e.target.naturalWidth / e.target.naturalHeight >= 1.8)}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#1B5E20]" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path d="M12 3v18M5 9c0-3 3-5 7-5s7 2 7 5-3 5-7 5-7-2-7-5Z" />
                    </svg>
                )}
            </span>
            <span className="leading-[1.02] text-left min-w-0">
                <span className="block text-[20px] sm:text-[26px] font-extrabold text-[#111827] tracking-[-0.01em]">
                    {String(app_name || "").split(" ")[0]}
                </span>
                {String(app_name || "").split(" ").length > 1 && (
                    <span className="block text-[13px] sm:text-[15px] font-medium text-[#6B7280]">
                        {String(app_name).split(" ").slice(1).join(" ")}
                    </span>
                )}
            </span>
        </span>
    );
};

const menuItemCls =
    "cursor-pointer rounded-lg px-3 py-2 text-[13.5px] font-medium text-[#4B5563] hover:!bg-[#6D28D9]/8 hover:!text-[#6D28D9] focus:!bg-[#6D28D9]/8 focus:!text-[#6D28D9] transition-colors";

// ---------- Desktop nav item ----------
function DesktopNavItem({ item, label }) {
    const location = useLocation();
    const navigate = useNavigate();
    const testid = `landing-nav-${item.id}`;
    const hasMenu = !!(item.children || item.sections);

    const isActive = item.to
        ? (item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to))
        : flatChildren(item).some((c) => isChildActive(c.to, location.pathname));

    const btnCls = `relative whitespace-nowrap text-[13px] xl:text-[14px] font-medium px-1.5 xl:px-2.5 py-2 transition-colors inline-flex items-center ${
        isActive
            ? "text-[#EA580C] font-semibold"
            : "text-[#374151] hover:text-[#111827]"
    }`;

    if (hasMenu) {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger
                    data-testid={testid}
                    className={`${btnCls} focus:outline-none data-[state=open]:text-[#111827]`}
                >
                    <span>{label}</span>
                    {isActive && (
                        <span className="absolute left-1.5 right-1.5 xl:left-2.5 xl:right-2.5 -bottom-0.5 h-[2px] rounded-full bg-[#EA580C]" />
                    )}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    sideOffset={10}
                    data-testid={`${testid}-menu`}
                    className={`rounded-2xl border-[#E3E0D3] shadow-[0_18px_50px_rgba(76,29,149,0.14)] p-2 bg-white ${
                        item.sections ? "w-[min(94vw,880px)] max-h-[76vh] overflow-y-auto" : "min-w-[240px]"
                    }`}
                >
                    {item.sections ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-2 gap-y-1">
                            {item.sections.map((sec) => (
                                <div key={sec.heading}>
                                    <DropdownMenuLabel className="px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#8B9B8D]">
                                        {sec.heading}
                                    </DropdownMenuLabel>
                                    {sec.items.map((c) => (
                                        <DropdownMenuItem
                                            key={c.to}
                                            data-testid={`${testid}-item-${slug(c.defaultLabel || c.label)}`}
                                            onSelect={() => goTo(navigate, c.to)}
                                            className={menuItemCls}
                                        >
                                            {c.label}
                                        </DropdownMenuItem>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        item.children.map((c) => (
                            <DropdownMenuItem
                                key={c.to}
                                data-testid={`${testid}-item-${slug(c.defaultLabel || c.label)}`}
                                onSelect={() => goTo(navigate, c.to)}
                                className={menuItemCls}
                            >
                                {c.label}
                            </DropdownMenuItem>
                        ))
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <Link to={item.to} data-testid={testid} className={btnCls}>
            <span>{label}</span>
            {isActive && (
                <span className="absolute left-2.5 right-2.5 xl:left-3.5 xl:right-3.5 -bottom-0.5 h-[2px] rounded-full bg-[#EA580C]" />
            )}
        </Link>
    );
}

// ---------- Mobile nav item (full-width accordion) ----------
function MobileNavItem({ item, label, onPick }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const testid = `landing-mobile-${item.id}`;
    const hasMenu = !!(item.children || item.sections);

    if (hasMenu) {
        return (
            <div className="border-b border-[#E3E0D3]" data-testid={`${testid}-group`}>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid={`${testid}-toggle`}
                    className="w-full flex items-center justify-between py-3.5 text-[15px] font-semibold text-[#4C1D95]"
                    aria-expanded={open}
                >
                    <span>{label}</span>
                    <ChevronRight
                        className={`h-4 w-4 text-[#8FB56A] transition-transform ${open ? "rotate-90" : ""}`}
                    />
                </button>
                {open && (
                    <div className="pb-3" data-testid={`${testid}-panel`}>
                        {(item.sections || [{ items: item.children }]).map((sec, i) => (
                            <div key={sec.heading || i}>
                                {sec.heading && (
                                    <p className="pl-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#8B9B8D]">
                                        {sec.heading}
                                    </p>
                                )}
                                <ul className="pl-3 flex flex-col gap-0.5">
                                    {sec.items.map((c) => (
                                        <li key={c.to}>
                                            <button
                                                type="button"
                                                onClick={() => { onPick(); goTo(navigate, c.to); }}
                                                data-testid={`${testid}-item-${slug(c.defaultLabel || c.label)}`}
                                                className="block w-full text-left py-2 pr-2 text-[13.5px] font-medium text-[#4B5563] hover:text-[#6D28D9] transition-colors"
                                            >
                                                {c.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className="border-b border-[#E3E0D3]">
            <Link
                to={item.to}
                onClick={onPick}
                data-testid={testid}
                className="block py-3.5 text-[15px] font-semibold text-[#4C1D95] hover:text-[#6D28D9] transition-colors"
            >
                {label}
            </Link>
        </div>
    );
}

// Paths that render their own Back control (avoid a double back row).
const OWN_BACK = ["/", "/info/", "/forms/", "/service/", "/login", "/signup"];

export const LandingHeader = () => {
    const navigate = useNavigate();
    const NAV_BASE = useUiStructure(NAV_STRUCTURE, "landing.nav");
    const { support } = useBranding();
    // Admin-configured support contact (Admin → Settings → Support contact) wins over the static fallbacks.
    const NAV = NAV_BASE.map((g) => g.id !== "help" || !g.children ? g : {
        ...g,
        children: g.children.map((c) =>
            c.to === SUPPORT_WHATSAPP && support?.whatsapp ? { ...c, to: `https://wa.me/${support.whatsapp.replace(/\D/g, "")}` }
            : c.to === SUPPORT_EMAIL && support?.email ? { ...c, to: `mailto:${support.email}` } : c),
    });
    const { pathname } = useLocation();
    const showBack = !OWN_BACK.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)));
    const { user } = useAuth();
    const { t } = useLang();
    const loggedIn = !!user;
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Lightweight ping so backend nav-links CMS override keeps working for
    // observability; grouped structure itself is client-driven.
    useEffect(() => {
        axios.get(`${API}/landing/nav-links`).catch(() => {});
    }, []);

    // Lock body scroll when the mobile panel is open
    useEffect(() => {
        if (drawerOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
        return undefined;
    }, [drawerOpen]);

    return (
        <header className="sticky top-0 z-50 w-full bg-white border-b border-[#E5E7EB]">
            <div className="w-full px-4 sm:px-6 lg:px-8 h-[68px] sm:h-[76px] flex items-center gap-3">
                <Link to="/" data-testid="landing-brand" aria-label="Family Repository home" className="min-w-0 shrink-0">
                    <LandingLogo />
                </Link>

                <nav
                    className="hidden lg:flex items-center gap-0 xl:gap-1 ml-3 xl:ml-6 flex-1 min-w-0 overflow-hidden"
                    data-testid="landing-desktop-nav"
                >
                    {NAV.map((item) => (
                        <DesktopNavItem key={item.id} item={item} label={item.labelKey ? t(item.labelKey) : item.label} />
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto lg:ml-0">
                    <div className="hidden sm:flex items-center gap-2">
                        <AccessibilityMenu compact short />
                        <LangSwitcher compact />
                    </div>
                    {!loggedIn && (
                        <button
                            type="button"
                            data-testid="landing-header-login"
                            onClick={() => navigate("/login")}
                            className="hidden xl:inline-flex items-center text-[14px] font-semibold text-[#EA580C] underline underline-offset-[3px] decoration-[1.5px] hover:text-[#C2410C] transition-colors px-1"
                        >
                            {t("navLogin")}
                        </button>
                    )}
                    <Button
                        data-testid="landing-header-cta"
                        onClick={() => navigate(loggedIn ? "/dashboard" : "/signup")}
                        className="vt-flashy bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-full px-5 sm:px-7 h-10 sm:h-11 font-semibold text-[13px] sm:text-[14.5px] shadow-none transition-colors"
                    >
                        {loggedIn ? t("navDashboard") : t("navStartFree")}
                    </Button>
                    {/* Hamburger — always visible (desktop + mobile) */}
                    <button
                        type="button"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open menu"
                        data-testid="landing-header-menu"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-[#111827] hover:bg-[#F3F4F6] transition-colors"
                    >
                        <Menu className="h-6 w-6" strokeWidth={2} />
                    </button>
                </div>
            </div>

            {/* Mobile full-width panel menu */}
            {drawerOpen && (
                <div
                    className="fixed inset-0 z-[60] bg-white flex flex-col"
                    data-testid="landing-mobile-drawer"
                >
                    <div className="h-[68px] shrink-0 flex items-center justify-between px-4 sm:px-6 border-b border-[#E5E7EB]">
                        <LandingLogo />
                        <button
                            type="button"
                            onClick={() => setDrawerOpen(false)}
                            aria-label="Close"
                            data-testid="landing-mobile-drawer-close"
                            className="h-10 w-10 inline-flex items-center justify-center rounded-full text-[#6D28D9] hover:bg-[#6D28D9]/10"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <nav
                        className="flex-1 overflow-y-auto px-5 py-2 flex flex-col w-full max-w-3xl mx-auto"
                        data-testid="landing-mobile-nav"
                    >
                        {NAV.map((item) => (
                            <MobileNavItem
                                key={item.id}
                                item={item}
                                label={item.labelKey ? t(item.labelKey) : item.label}
                                onPick={() => setDrawerOpen(false)}
                            />
                        ))}
                    </nav>
                    <div className="p-4 border-t border-[#E3E0D3] space-y-2.5 shrink-0 w-full max-w-3xl mx-auto">
                        <div className="pb-1 flex items-center gap-2">
                            <LangSwitcher />
                            <AccessibilityMenu />
                        </div>
                        {!loggedIn && (
                            <Button
                                data-testid="landing-mobile-login"
                                variant="outline"
                                onClick={() => { setDrawerOpen(false); navigate("/login"); }}
                                className="w-full h-11 rounded-full border-[#6D28D9]/30 text-[#6D28D9]"
                            >
                                {t("navLogin")}
                            </Button>
                        )}
                        <Button
                            data-testid="landing-mobile-cta"
                            onClick={() => { setDrawerOpen(false); navigate(loggedIn ? "/dashboard" : "/signup"); }}
                            className="w-full h-11 rounded-full bg-[#6D28D9] hover:bg-[#5B21B6] text-white font-semibold"
                        >
                            {loggedIn ? t("navDashboard") : t("navStartFree")}
                        </Button>
                    </div>
                </div>
            )}
            {showBack && (
                <div className="w-full bg-[#FAFAFA] border-t border-[#F1F1F3]">
                    <div className="w-full px-4 sm:px-6 lg:px-8 h-10 flex items-center">
                        <button
                            type="button"
                            onClick={() => safeBack(navigate, "/")}
                            data-testid="public-page-back"
                            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] hover:text-[#5B21B6] transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" /> {t("back")}
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};
